import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, ChevronLeft, ChevronRight, X, ExternalLink,
  Calendar, DollarSign, User, Car, Clock, Shield,
  FileText, AlertTriangle, Gavel, Eye, ChevronDown
} from 'lucide-react';

const STATUS_CONFIG = {
  requested:                { label: 'Requested',        color: 'bg-blue-100 text-blue-700' },
  approved_pending_payment: { label: 'Awaiting Payment', color: 'bg-amber-100 text-amber-700' },
  paid:                     { label: 'Paid',             color: 'bg-green-100 text-green-700' },
  prehire_in_progress:      { label: 'Pre-Hire',        color: 'bg-cyan-100 text-cyan-700' },
  active:                   { label: 'Active',           color: 'bg-emerald-100 text-emerald-700' },
  posthire_in_progress:     { label: 'Post-Hire',       color: 'bg-purple-100 text-purple-700' },
  posthire_submitted:       { label: 'Post-Hire Done',  color: 'bg-violet-100 text-violet-700' },
  cooldown_window_open:     { label: 'Cooldown',        color: 'bg-orange-100 text-orange-700' },
  completed:                { label: 'Completed',        color: 'bg-slate-100 text-slate-600' },
  disputed:                 { label: 'Disputed',         color: 'bg-red-100 text-red-700' },
  cancelled:                { label: 'Cancelled',        color: 'bg-gray-100 text-gray-500' },
  expired:                  { label: 'Expired',          color: 'bg-gray-100 text-gray-400' },
};

const BOND_CONFIG = {
  pending:    { label: 'Pending',    color: 'bg-slate-100 text-slate-600' },
  authorized: { label: 'Authorized', color: 'bg-blue-100 text-blue-700' },
  captured:   { label: 'Captured',   color: 'bg-amber-100 text-amber-700' },
  released:   { label: 'Released',   color: 'bg-green-100 text-green-700' },
  refunded:   { label: 'Refunded',   color: 'bg-teal-100 text-teal-700' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);
const PAGE_SIZE = 15;

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function BondBadge({ status }) {
  if (!status) return <span className="text-xs text-slate-400">-</span>;
  const cfg = BOND_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
}

function formatCurrency(v) {
  if (v == null) return '-';
  return `$${Number(v).toFixed(2)}`;
}

// ─── Detail Drawer ───────────────────────────────────────────────────────────

function BookingDetailDrawer({ booking, onClose, api }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!booking) return;
    setLoading(true);
    api.get(`/bookings/${booking.booking_id}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(booking))
      .finally(() => setLoading(false));
  }, [booking, api]);

  if (!booking) return null;
  const d = detail || booking;

  const vehicleName = d.vehicle ? `${d.vehicle.make || ''} ${d.vehicle.model || ''}`.trim() : 'Unknown Vehicle';
  const customerName = d.customer?.full_name || d.customer_id || 'Unknown';
  const customerEmail = d.customer?.email || '';
  const hostName = d.vehicle?.owner_name || d.owner_id || 'Unknown';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
          <div>
            <p className="font-semibold text-sm text-slate-900">{d.booking_id}</p>
            <StatusBadge status={d.status} />
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-detail-drawer">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {loading ? (
          <div className="p-6 space-y-4">
            {[1,2,3,4].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Vehicle */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Car className="w-4 h-4" /> Vehicle</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p className="font-medium">{vehicleName}</p>
                <p className="text-slate-500">{d.vehicle_id}</p>
              </CardContent>
            </Card>

            {/* People */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4" /> People</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Host</p>
                  <p className="font-medium">{hostName}</p>
                  <p className="text-slate-500 text-xs">{d.owner_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Renter</p>
                  <p className="font-medium">{customerName}</p>
                  {customerEmail && <p className="text-slate-500 text-xs">{customerEmail}</p>}
                  <p className="text-slate-500 text-xs">{d.customer_id}</p>
                </div>
              </CardContent>
            </Card>

            {/* Dates & Location */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4" /> Trip Details</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Start</span><span>{formatDate(d.start_date)} {d.pickup_time || ''}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">End</span><span>{formatDate(d.end_date)} {d.return_time || ''}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Days</span><span>{d.total_days || '-'}</span></div>
                {d.pickup_location && <div className="flex justify-between"><span className="text-slate-500">Pickup</span><span className="text-right text-xs max-w-[200px]">{d.pickup_location}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Created</span><span>{formatDate(d.created_at)}</span></div>
              </CardContent>
            </Card>

            {/* Financial */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4" /> Financials</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Daily Rate</span><span>{formatCurrency(d.daily_rate)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{formatCurrency(d.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Platform Fee</span><span>{formatCurrency(d.platform_fee)}</span></div>
                <div className="flex justify-between font-medium border-t pt-1"><span>Total</span><span>{formatCurrency(d.total_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Host Payout</span><span>{formatCurrency(d.owner_payout)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment Status</span><span className="capitalize">{d.payment_status || '-'}</span></div>
              </CardContent>
            </Card>

            {/* Bond */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> Bond</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>{formatCurrency(d.bond_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status</span><BondBadge status={d.bond_status} /></div>
                <div className="flex justify-between"><span className="text-slate-500">Mode</span><span className="capitalize">{d.bond_mode || '-'}</span></div>
              </CardContent>
            </Card>

            {/* Flags */}
            {d.is_guest_checkout_booking && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5" /> Guest checkout booking
              </div>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(`/admin/bookings/${d.booking_id}/evidence`, '_blank')} data-testid="action-evidence">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Evidence
                </Button>
                <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(`/bookings/${d.booking_id}`, '_blank')} data-testid="action-view-booking">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Full Booking
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function BookingOversight() {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('skip', String(page * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      const res = await api.get(`/admin/bookings?${params.toString()}`);
      setBookings(res.data.bookings || []);
      setTotal(res.data.total || 0);
    } catch {
      setBookings([]);
      setTotal(0);
    }
    setLoading(false);
  }, [api, statusFilter, page]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Client-side search filter (booking ID, host, renter, vehicle)
  const filtered = searchQuery
    ? bookings.filter(b => {
        const q = searchQuery.toLowerCase();
        return (
          (b.booking_id || '').toLowerCase().includes(q) ||
          (b.customer?.full_name || '').toLowerCase().includes(q) ||
          (b.customer?.email || '').toLowerCase().includes(q) ||
          (b.owner_id || '').toLowerCase().includes(q) ||
          (b.vehicle?.make || '').toLowerCase().includes(q) ||
          (b.vehicle?.model || '').toLowerCase().includes(q)
        );
      })
    : bookings;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const statusCounts = {};
  bookings.forEach(b => { statusCounts[b.status] = (statusCounts[b.status] || 0) + 1; });

  return (
    <PlatformOwnerAppShell activeId="bookings" title="Booking Oversight">
      <div className="space-y-4" data-testid="booking-oversight-page">
        <HostDirectModeBanner variant="subtle" context="generic" />
        {/* Stats Bar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Booking Oversight</h2>
            <p className="text-sm text-slate-500">{total} total bookings</p>
          </div>
          <Badge variant="secondary" className="text-xs">{statusFilter ? STATUS_CONFIG[statusFilter]?.label || statusFilter : 'All statuses'}</Badge>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2" data-testid="booking-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search booking ID, renter, vehicle..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 text-sm"
              data-testid="booking-search-input"
            />
          </div>
          {/* Status Filter Dropdown */}
          <div className="relative">
            <Button
              variant="outline"
              className="w-full sm:w-auto text-sm justify-between min-w-[180px]"
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              data-testid="status-filter-btn"
            >
              {statusFilter ? (STATUS_CONFIG[statusFilter]?.label || statusFilter) : 'All Statuses'}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            {statusDropdownOpen && (
              <div className="absolute z-20 mt-1 w-56 bg-white border rounded-lg shadow-lg max-h-72 overflow-y-auto right-0">
                <button
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${!statusFilter ? 'bg-slate-100 font-medium' : ''}`}
                  onClick={() => { setStatusFilter(''); setPage(0); setStatusDropdownOpen(false); }}
                  data-testid="filter-all"
                >All Statuses</button>
                {ALL_STATUSES.map(s => (
                  <button
                    key={s}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between ${statusFilter === s ? 'bg-slate-100 font-medium' : ''}`}
                    onClick={() => { setStatusFilter(s); setPage(0); setStatusDropdownOpen(false); }}
                    data-testid={`filter-${s}`}
                  >
                    <StatusBadge status={s} />
                  </button>
                ))}
              </div>
            )}
          </div>
          {(statusFilter || searchQuery) && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setStatusFilter(''); setSearchQuery(''); setPage(0); }} data-testid="clear-filters-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-400" data-testid="empty-state">
                <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No bookings found</p>
                <p className="text-sm mt-1">{statusFilter ? 'Try a different status filter.' : 'No booking data available.'}</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm" data-testid="bookings-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Booking ID</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Vehicle</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Renter</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Dates</th>
                        <th className="text-right px-4 py-3 font-medium text-slate-600">Total</th>
                        <th className="text-center px-4 py-3 font-medium text-slate-600">Bond</th>
                        <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.map(b => (
                        <tr key={b.booking_id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedBooking(b)} data-testid={`booking-row-${b.booking_id}`}>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-slate-600">{b.booking_id?.slice(0, 16)}...</span>
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                          <td className="px-4 py-3">
                            <span className="text-slate-900">{b.vehicle ? `${b.vehicle.make} ${b.vehicle.model}` : '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-slate-900">{b.customer?.full_name || '-'}</span>
                            {b.customer?.email && <p className="text-xs text-slate-400">{b.customer.email}</p>}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {formatDate(b.start_date)} - {formatDate(b.end_date)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(b.total_amount)}</td>
                          <td className="px-4 py-3 text-center"><BondBadge status={b.bond_status} /></td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" className="text-xs" onClick={e => { e.stopPropagation(); setSelectedBooking(b); }} data-testid={`view-${b.booking_id}`}>
                              <Eye className="w-3.5 h-3.5 mr-1" /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y">
                  {filtered.map(b => (
                    <div key={b.booking_id} className="p-4 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedBooking(b)} data-testid={`booking-card-${b.booking_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-mono text-xs text-slate-500">{b.booking_id?.slice(0, 20)}...</p>
                          <p className="font-medium text-sm mt-0.5">{b.vehicle ? `${b.vehicle.make} ${b.vehicle.model}` : 'Unknown'}</p>
                        </div>
                        <StatusBadge status={b.status} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{b.customer?.full_name || '-'}</span>
                        <span className="font-medium text-slate-900">{formatCurrency(b.total_amount)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
                        <span>{formatDate(b.start_date)} - {formatDate(b.end_date)}</span>
                        <BondBadge status={b.bond_status} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between" data-testid="booking-pagination">
            <p className="text-sm text-slate-500">Page {page + 1} of {totalPages} ({total} bookings)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedBooking && (
        <BookingDetailDrawer
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          api={api}
        />
      )}
    </PlatformOwnerAppShell>
  );
}
